import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
} from 'react-query';
import { useSelector } from 'react-redux';
import {
  getStudentSkillLevelReport,
  getSectionSkillLevelReport,
  getTeacherSkillLevelReport,
  getCourseSkillLevelReport,
  getDistrictSkillLevelReport,
  getCourseSkillLevelReportsBatch,
  getTeacherSkillLevelReportsBatch,
  getStudentSkillLevelReportsBatch,
  getDistrictPMReport,
  getTeacherPMReport,
} from '@apis/reports';
import {
    getAvailableReports,
    generateReports,
    getUserPastReports,
    getFeatureAccess,
} from '../../apis/adminReports';
import { getUserId } from '../../reducers/user/userReducer';
import { AvailableReportsData } from './types';

interface PastReport {
    id: number;
    report_name: string;
    report_type: string;
    report_extension: string;
    path: string;
    last_generated: string;
    status: string;
    user: number;
    report: number;
    report_url: string;
}

interface PastReportsResponse {
    results: PastReport[];
    url_ttl?: number;
}

interface GenerateReportsPayload {
    district?: {
        category: number;
        ids: number[];
    };
    course?: {
        category: number;
        ids: number[];
    };
}

export const useAvailableReportsQuery = (): UseQueryResult<AvailableReportsData, Error> => {
    const userId = useSelector(getUserId);
    
    const result = useQuery<AvailableReportsData, Error>(
        ['availableReportsList', userId],
        async () => {
            const res: any = await getAvailableReports();
            return res.response?.data || {};
        },
        {
            enabled: !!userId,
            placeholderData: {} as AvailableReportsData,
            staleTime: 60 * 60 * 1000,
            refetchOnWindowFocus: false,
            cacheTime: 60 * 60 * 1000,
        },
    );
    return result;
};

export const useReportsFeatureAccessQuery = (feature: string): boolean => {
    const userId = useSelector(getUserId);
    
    const result = useQuery<boolean, Error>(
        ['reportsFeatureAccess', feature, userId],
        async () => {
            const res: any = await getFeatureAccess(feature);
            const userAccess = res.response?.data?.user_access;
            
            // If no user_access object, return false
            if (!userAccess || typeof userAccess !== 'object') {
                return false;
            }
            
            // Check if any entity has access
            const hasAccess = Object.values(userAccess).some(
                (access: any) => access?.has_access === true
            );
            
            return hasAccess;
        },
        {
            enabled: !!userId,
            placeholderData: false,
            staleTime: 60 * 60 * 1000,
        },
    );
    return result.data ?? false;
};

export const useGenerateReportsMutation = (): UseMutationResult<any, Error, GenerateReportsPayload> => {
    const queryClient = useQueryClient();
    const userId = useSelector(getUserId);
    
    return useMutation({
        mutationFn: (payload: GenerateReportsPayload) => generateReports(payload),
        onSuccess: () => {
            // Invalidate past reports query to refresh the list when a new report is generated
            if (userId) {
                queryClient.invalidateQueries(['pastReportsList', userId]);
            }
        },
    });
};

export const useGetStudentSkillLevelReportMutation = (): UseMutationResult<
  any,
  Error,
  {
    studentId: number;
    curriculumId: number;
    studentName?: string;
    curriculumName?: string;
  }
> => {
  const queryClient = useQueryClient();
  const userId = useSelector(getUserId);

  return useMutation({
    mutationFn: (payload: {
      studentId: number;
      curriculumId: number;
      studentName?: string;
      curriculumName?: string;
    }) =>
      getStudentSkillLevelReport(
        payload.studentId,
        payload.curriculumId,
        payload.studentName,
        payload.curriculumName,
      ),
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries(['pastReportsList', userId]);
      }
    },
  });
};

export const useGetStudentSkillLevelReportsBatchMutation = (): UseMutationResult<
  any,
  Error,
  {
    reports: { studentId: number; curriculumId: number }[];
  }
> => {
  const queryClient = useQueryClient();
  const userId = useSelector(getUserId);

  return useMutation({
    mutationFn: (payload: { reports: { studentId: number; curriculumId: number }[] }) =>
      getStudentSkillLevelReportsBatch(payload.reports),
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries(['pastReportsList', userId]);
      }
    },
  });
};

export const useGetSectionSkillLevelReportMutation = (): UseMutationResult<
  any,
  Error,
  {
    sectionId: number;
    courseId: number;
    sectionName?: string;
    courseName?: string;
  }
> => {
  const queryClient = useQueryClient();
  const userId = useSelector(getUserId);

  return useMutation({
    mutationFn: (payload: {
      sectionId: number;
      courseId: number;
      sectionName?: string;
      courseName?: string;
    }) =>
      getSectionSkillLevelReport(
        payload.sectionId,
        payload.courseId,
        payload.sectionName,
        payload.courseName,
      ),
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries(['pastReportsList', userId]);
      }
    },
  });
};

export const useGetTeacherSkillLevelReportMutation = (): UseMutationResult<
  any,
  Error,
  {
    teacherId: number;
    curriculumId: number;
  }
> => {
  const queryClient = useQueryClient();
  const userId = useSelector(getUserId);

  return useMutation({
    mutationFn: (payload: { teacherId: number; curriculumId: number }) =>
      getTeacherSkillLevelReport(payload.teacherId, payload.curriculumId),
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries(['pastReportsList', userId]);
      }
    },
  });
};

export const useGetCourseSkillLevelReportMutation = (): UseMutationResult<
  any,
  Error,
  {
    courseId: number;
  }
> => {
  const queryClient = useQueryClient();
  const userId = useSelector(getUserId);

  return useMutation({
    mutationFn: (payload: { courseId: number }) =>
      getCourseSkillLevelReport(payload.courseId),
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries(['pastReportsList', userId]);
      }
    },
  });
};

export const useGetCourseSkillLevelReportsBatchMutation = (): UseMutationResult<
  any,
  Error,
  {
    courseIds: number[];
  }
> => {
  const queryClient = useQueryClient();
  const userId = useSelector(getUserId);

  return useMutation({
    mutationFn: (payload: { courseIds: number[] }) =>
      getCourseSkillLevelReportsBatch(payload.courseIds),
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries(['pastReportsList', userId]);
      }
    },
  });
};

export const useGetTeacherSkillLevelReportsBatchMutation = (): UseMutationResult<
  any,
  Error,
  {
    reports: { teacherId: number; curriculumId: number }[];
  }
> => {
  const queryClient = useQueryClient();
  const userId = useSelector(getUserId);

  return useMutation({
    mutationFn: (payload: { reports: { teacherId: number; curriculumId: number }[] }) =>
      getTeacherSkillLevelReportsBatch(payload.reports),
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries(['pastReportsList', userId]);
      }
    },
  });
};

export const useGetDistrictSkillLevelReportMutation = (): UseMutationResult<
  any,
  Error,
  {
    districtId: number;
    curriculumId: number;
    districtName?: string;
    curriculumName?: string;
  }
> => {
  const queryClient = useQueryClient();
  const userId = useSelector(getUserId);

  return useMutation({
    mutationFn: (payload: {
      districtId: number;
      curriculumId: number;
      districtName?: string;
      curriculumName?: string;
    }) =>
      getDistrictSkillLevelReport(
        payload.districtId,
        payload.curriculumId,
      ),
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries(['pastReportsList', userId]);
      }
    },
  });
};

export const usePastReportsQuery = (): UseQueryResult<PastReportsResponse, Error> => {
  const userId = useSelector(getUserId);
  const queryClient = useQueryClient();

  const cachedData = queryClient.getQueryData<PastReportsResponse>([
    'pastReportsList',
    userId,
  ]);
  const ttlMs = cachedData?.url_ttl ? cachedData.url_ttl * 1000 : 30 * 60 * 1000;

  const result = useQuery<PastReportsResponse, Error>(
    ['pastReportsList', userId],
    async (): Promise<PastReportsResponse> => {
      const res: any = await getUserPastReports();
      const data = res.response?.data || { results: [] };
      return data as PastReportsResponse;
    },
    {
      enabled: !!userId,
      staleTime: ttlMs,
      cacheTime: ttlMs,
      refetchOnWindowFocus: true,
      placeholderData: {
        results: [],
      },
      onSuccess: (data) => {
        // Update query defaults with TTL from API response
        const urlTtlMs = data.url_ttl ? data.url_ttl * 1000 : 30 * 60 * 1000;
        queryClient.setQueryDefaults(['pastReportsList', userId], {
          staleTime: urlTtlMs,
          cacheTime: urlTtlMs,
        });
      },
    },
  );
  return result;
};


export const useGetDistrictPMReportMutation = (): UseMutationResult<
  any,
  Error,
  { districtId: number; districtName?: string }
> => {
  const queryClient = useQueryClient();
  const userId = useSelector(getUserId);

  return useMutation({
    mutationFn: (payload: { districtId: number; districtName?: string }) =>
      getDistrictPMReport(payload.districtId),
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries(['pastReportsList', userId]);
      }
    },
  });
};

export const useGetTeacherPMReportMutation = (): UseMutationResult<
  any,
  Error,
  { teacherId: number; teacherName?: string }
> => {
  const queryClient = useQueryClient();
  const userId = useSelector(getUserId);

  return useMutation({
    mutationFn: (payload: { teacherId: number; teacherName?: string }) =>
      getTeacherPMReport(payload.teacherId),
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries(['pastReportsList', userId]);
      }
    },
  });
};
